import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import {
    countAnsweredQuestionnaireQuestions,
    createDefaultQuestionnaireState,
    normalizeQuestionnaireState,
    QUESTIONNAIRE_QUESTIONS,
    QuestionnaireChannelId,
    QuestionnaireAnswerId,
    QuestionnaireGeneralSettingId,
    QuestionnaireRuntimeMarketplaceId,
    QuestionnaireState,
    SettingsTabId,
    EDITABLE_SETTINGS_TAB_IDS,
    EditableSettingsTabId,
    isEditableSettingsTab,
} from '@/components/settings-questionnaire-modal/settings-questionnaire.schema';
import { useAuth } from '@/context/auth.context';
import { useClientFeatureControls } from '@/context/client-feature-controls.context';
import { useMarketplaceApi } from '@/context/marketplace.context';
import {
    closeReservedExternalPostTarget,
    reserveExternalPostTarget,
    submitExternalPostFormToTarget,
} from '@/client/external-post-form';
import {
    subscribeToClientBillingPaymentFailureNotifications,
    subscribeToClientBillingPaymentSuccessNotifications,
    type ClientBillingPaymentFailureNotification,
    type ClientBillingPaymentSuccessNotification,
} from '@/client/client-billing-payment-notifications';
import {
    publishClientSettingsSaveNotification,
    subscribeToClientSettingsSaveNotifications,
} from '@/client/client-settings-save-notifications';
import { ClientSettingsUpdateEntity, ClientSettingsUpdateSectionsEntity } from '@/entities/client-settings.entity';
import { ClientBillingSummaryEntity } from '@/entities/client-billing.entity';
import { DEFAULT_BILLING_CURRENCY, type BillingCurrency } from '@mrw/billing-money';
import {
    getClientBillingPackageGrantedAmounts,
    getFirstClientBillingValidationMessage,
    validateClientBillingRobokassaCheckoutInput,
} from '@/shared/client-billing';
import { normalizeClientSettingsEntity } from '@/shared/client-settings';
import {
    ApiError,
    extractApiErrorDescriptor,
    extractApiErrorMessageFromUnknown,
    extractApiValidationErrorMessageFromUnknown,
} from '@/shared/errors/api-error';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/ui/toast';
import { createBillingPaymentToastId } from '@/ui/toast-ids';

type UseSettingsQuestionnaireModalControllerArgs = {
    isShown: boolean;
    setIsShown: (value: boolean) => void;
};

const settingsQuestionnaireLogger = createClientLogger({
    scope: 'settings-questionnaire',
});

const PAYMENT_SUCCESS_REFRESH_ATTEMPTS = 5;
const PAYMENT_SUCCESS_REFRESH_DELAY_MS = 1500;

export type BillingPaymentFormState = {
    isOpen: boolean;
    isSubmitting: boolean;
    packageId: string;
    email: string;
    amountMinorUnits: number;
    currency: BillingCurrency;
    tokenAmount: number;
    validationMessage: string | null;
};

export type SaveSettingsQuestionnaireTabOptions = {
    closeAfterSave?: boolean;
    successMessage?: string | null;
};

export type SettingsQuestionnaireSaveConflictState = {
    message: string;
    currentVersion?: string;
    source: 'server' | 'browser';
};

export type SettingsQuestionnaireModalControllerBindings = {
    formState: QuestionnaireState;
    paymentForm: BillingPaymentFormState;
    questions: typeof QUESTIONNAIRE_QUESTIONS;
    isLoading: boolean;
    hasLoadedSettings: boolean;
    isSaving: boolean;
    saveConflict: SettingsQuestionnaireSaveConflictState | null;
    dirtyTabIds: EditableSettingsTabId[];
    completionProgress: string;
    onStoreNameChanged: (value: string) => void;
    onCompanyInfoLinkChanged: (value: string) => void;
    onEmailChanged: (value: string) => void;
    onCommentsChanged: (value: string) => void;
    onAnswerChanged: (questionId: QuestionnaireAnswerId, value: string) => void;
    onGeneralSettingChanged: (settingId: QuestionnaireGeneralSettingId, checked: boolean) => void;
    onChannelChanged: (channelId: QuestionnaireChannelId, checked: boolean) => void;
    onMarketplaceReviewImportLimitChanged: (marketplaceId: QuestionnaireRuntimeMarketplaceId, value: string) => void;
    openPaymentForm: (packageId?: string) => void;
    closePaymentForm: () => void;
    onPaymentPackageChanged: (packageId: string) => void;
    onPaymentEmailChanged: (value: string) => void;
    submitPayment: () => Promise<void>;
    saveTab: (tabId: SettingsTabId, options?: SaveSettingsQuestionnaireTabOptions) => Promise<boolean>;
    saveForm: (event: FormEvent<HTMLFormElement>, tabId: SettingsTabId) => Promise<void>;
    resetForm: (tabId: SettingsTabId) => void;
    closeModal: (tabId?: SettingsTabId) => void;
    reloadSettings: () => Promise<void>;
};

const createDefaultPaymentFormState = (
    currency: BillingCurrency = DEFAULT_BILLING_CURRENCY,
): BillingPaymentFormState => ({
    isOpen: false,
    isSubmitting: false,
    packageId: '',
    email: '',
    amountMinorUnits: 0,
    currency,
    tokenAmount: 0,
    validationMessage: null,
});

const findBillingPackage = (billing: ClientBillingSummaryEntity, packageId: string) =>
    billing.packages.find((billingPackage) => billingPackage.id === packageId);

const buildGeneralSettingsUpdate = (
    state: QuestionnaireState,
): NonNullable<ClientSettingsUpdateSectionsEntity['general']> => ({
    storeName: state.storeName.trim(),
    companyInfoLink: state.companyInfoLink.trim(),
    email: state.email.trim(),
    featureControls: {
        ...state.settings.general,
    },
});

const buildAiStrategySettingsUpdate = (
    state: QuestionnaireState,
    clearAiStrategy: boolean,
): NonNullable<ClientSettingsUpdateSectionsEntity['aiStrategy']> => ({
    answers: state.answers,
    comments: state.comments.trim(),
    ...(clearAiStrategy ? { clearAiStrategy: true } : {}),
});

const buildMarketplaceEnabledUpdate = (
    state: QuestionnaireState,
): NonNullable<ClientSettingsUpdateSectionsEntity['marketplaces']> => ({
    wb: {
        enabled: state.settings.marketplaces.wb.enabled,
    },
    ozon: {
        enabled: state.settings.marketplaces.ozon.enabled,
    },
});

const buildMarketplaceReviewImportLimitUpdate = (
    state: QuestionnaireState,
): NonNullable<ClientSettingsUpdateSectionsEntity['marketplaces']> => {
    const update: NonNullable<ClientSettingsUpdateSectionsEntity['marketplaces']> = {};

    if (state.settings.marketplaces.wb.enabled) {
        update.wb = {
            preExistingReviewImportLimit: state.settings.marketplaces.wb.preExistingReviewImportLimit,
        };
    }

    if (state.settings.marketplaces.ozon.enabled) {
        update.ozon = {
            preExistingReviewImportLimit: state.settings.marketplaces.ozon.preExistingReviewImportLimit,
        };
    }

    return update;
};

const mergeMarketplaceSettingsUpdate = (
    target: ClientSettingsUpdateSectionsEntity,
    patch: NonNullable<ClientSettingsUpdateSectionsEntity['marketplaces']>,
): void => {
    const nextMarketplaces = {
        ...(target.marketplaces ?? {}),
    };

    if (patch.wb) {
        nextMarketplaces.wb = {
            ...target.marketplaces?.wb,
            ...patch.wb,
        };
    }

    if (patch.ozon) {
        nextMarketplaces.ozon = {
            ...target.marketplaces?.ozon,
            ...patch.ozon,
        };
    }

    target.marketplaces = nextMarketplaces;
};

const areGeneralSectionsEqual = (left: QuestionnaireState, right: QuestionnaireState): boolean => {
    const leftGeneral = buildGeneralSettingsUpdate(left);
    const rightGeneral = buildGeneralSettingsUpdate(right);

    return (
        leftGeneral.storeName === rightGeneral.storeName &&
        leftGeneral.companyInfoLink === rightGeneral.companyInfoLink &&
        leftGeneral.email === rightGeneral.email &&
        leftGeneral.featureControls?.allowResponseSending === rightGeneral.featureControls?.allowResponseSending &&
        leftGeneral.featureControls?.allowAiAnswerGeneration ===
            rightGeneral.featureControls?.allowAiAnswerGeneration &&
        leftGeneral.featureControls?.allowAiAnalysis === rightGeneral.featureControls?.allowAiAnalysis &&
        leftGeneral.featureControls?.autoReviewAiAnalysisEnabled ===
            rightGeneral.featureControls?.autoReviewAiAnalysisEnabled &&
        leftGeneral.featureControls?.autoReviewProcessingEnabled ===
            rightGeneral.featureControls?.autoReviewProcessingEnabled
    );
};

const areChannelSectionsEqual = (left: QuestionnaireState, right: QuestionnaireState): boolean =>
    left.settings.marketplaces.wb.enabled === right.settings.marketplaces.wb.enabled &&
    left.settings.marketplaces.ozon.enabled === right.settings.marketplaces.ozon.enabled;

const areReviewImportSectionsEqual = (left: QuestionnaireState, right: QuestionnaireState): boolean =>
    left.settings.marketplaces.wb.preExistingReviewImportLimit ===
        right.settings.marketplaces.wb.preExistingReviewImportLimit &&
    left.settings.marketplaces.ozon.preExistingReviewImportLimit ===
        right.settings.marketplaces.ozon.preExistingReviewImportLimit;

const areAnswerSectionsEqual = (left: QuestionnaireState, right: QuestionnaireState): boolean => {
    const answerKeys = new Set([...Object.keys(left.answers), ...Object.keys(right.answers)]);

    for (const answerKey of answerKeys) {
        if (left.answers[answerKey as QuestionnaireAnswerId] !== right.answers[answerKey as QuestionnaireAnswerId]) {
            return false;
        }
    }

    return left.comments.trim() === right.comments.trim();
};

const isQuestionnaireTabDirty = (
    tabId: EditableSettingsTabId,
    persistedState: QuestionnaireState,
    currentState: QuestionnaireState,
): boolean => {
    if (tabId === 'general') {
        return !areGeneralSectionsEqual(persistedState, currentState);
    }

    if (tabId === 'channels') {
        return !areChannelSectionsEqual(persistedState, currentState);
    }

    if (tabId === 'review-import') {
        return !areReviewImportSectionsEqual(persistedState, currentState);
    }

    return !areAnswerSectionsEqual(persistedState, currentState);
};

const resolveDirtyEditableTabs = (
    persistedState: QuestionnaireState,
    currentState: QuestionnaireState,
): EditableSettingsTabId[] =>
    EDITABLE_SETTINGS_TAB_IDS.filter((tabId) => isQuestionnaireTabDirty(tabId, persistedState, currentState));

const buildSettingsUpdateRequest = ({
    baseVersion,
    tabIds,
    state,
    clearAiStrategy,
}: {
    baseVersion: string;
    tabIds: readonly EditableSettingsTabId[];
    state: QuestionnaireState;
    clearAiStrategy: boolean;
}): ClientSettingsUpdateEntity => {
    const sections: ClientSettingsUpdateSectionsEntity = {};

    if (tabIds.includes('general')) {
        sections.general = buildGeneralSettingsUpdate(state);
    }

    if (tabIds.includes('channels')) {
        mergeMarketplaceSettingsUpdate(sections, buildMarketplaceEnabledUpdate(state));
    }

    if (tabIds.includes('review-import')) {
        mergeMarketplaceSettingsUpdate(sections, buildMarketplaceReviewImportLimitUpdate(state));
    }

    if (tabIds.includes('ai-strategy')) {
        sections.aiStrategy = buildAiStrategySettingsUpdate(state, clearAiStrategy);
    }

    return {
        baseVersion,
        sections,
    };
};

const SETTINGS_CONFLICT_MESSAGE =
    'Настройки изменились в другой вкладке. Загрузите актуальные данные перед сохранением.';

const isSettingsVersionConflictError = (error: unknown): { currentVersion?: string } | null => {
    if (error instanceof ApiError) {
        if (error.status !== 409 || error.code !== 'SettingsVersionConflictError') {
            return null;
        }

        const details = error.details as { currentVersion?: unknown } | null;
        return {
            currentVersion: typeof details?.currentVersion === 'string' ? details.currentVersion : undefined,
        };
    }

    if (!error || typeof error !== 'object') {
        return null;
    }

    const response = (error as { response?: { status?: number; data?: unknown } }).response;
    const descriptor = extractApiErrorDescriptor(response?.data);
    if (response?.status !== 409 || descriptor?.code !== 'SettingsVersionConflictError') {
        return null;
    }

    const details = descriptor.details as { currentVersion?: unknown } | null;
    return {
        currentVersion: typeof details?.currentVersion === 'string' ? details.currentVersion : undefined,
    };
};

const mergeQuestionnaireTabState = (
    targetState: QuestionnaireState,
    sourceState: QuestionnaireState,
    tabId: SettingsTabId,
): QuestionnaireState => {
    if (tabId === 'general') {
        return {
            ...targetState,
            storeName: sourceState.storeName,
            companyInfoLink: sourceState.companyInfoLink,
            email: sourceState.email,
            settings: {
                ...targetState.settings,
                general: {
                    ...sourceState.settings.general,
                },
            },
        };
    }

    if (tabId === 'channels') {
        return {
            ...targetState,
            settings: {
                ...targetState.settings,
                marketplaces: {
                    wb: {
                        ...targetState.settings.marketplaces.wb,
                        enabled: sourceState.settings.marketplaces.wb.enabled,
                    },
                    ozon: {
                        ...targetState.settings.marketplaces.ozon,
                        enabled: sourceState.settings.marketplaces.ozon.enabled,
                    },
                },
            },
        };
    }

    if (tabId === 'review-import') {
        return {
            ...targetState,
            settings: {
                ...targetState.settings,
                marketplaces: {
                    wb: {
                        ...targetState.settings.marketplaces.wb,
                        preExistingReviewImportLimit: sourceState.settings.marketplaces.wb.preExistingReviewImportLimit,
                    },
                    ozon: {
                        ...targetState.settings.marketplaces.ozon,
                        preExistingReviewImportLimit:
                            sourceState.settings.marketplaces.ozon.preExistingReviewImportLimit,
                    },
                },
            },
        };
    }

    if (tabId === 'ai-strategy') {
        return {
            ...targetState,
            answers: {
                ...sourceState.answers,
            },
            comments: sourceState.comments,
        };
    }

    return targetState;
};

const resolveBillingPackageId = (billing: ClientBillingSummaryEntity, packageId?: string): string => {
    if (packageId && findBillingPackage(billing, packageId)) {
        return packageId;
    }

    return billing.packages[0]?.id ?? '';
};

const wait = (delayMs: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, delayMs));

const hasPaymentMatchingNotification = (
    billing: ClientBillingSummaryEntity,
    notification: ClientBillingPaymentSuccessNotification,
): boolean => {
    if (!notification.invoiceId) {
        return true;
    }

    const expectedOperationId = `robokassa-${notification.invoiceId}`;
    return billing.payments.some((payment) => payment.operationId === expectedOperationId);
};

export const useSettingsQuestionnaireModalController = ({
    isShown,
    setIsShown,
}: UseSettingsQuestionnaireModalControllerArgs): SettingsQuestionnaireModalControllerBindings => {
    const { accessToken, clientId } = useAuth();
    const { setAiQuota, setFeatureControls } = useClientFeatureControls();
    const { settingsApi, billingApi } = useMarketplaceApi();
    const [formState, setFormState] = useState<QuestionnaireState>(createDefaultQuestionnaireState);
    const [persistedFormState, setPersistedFormState] = useState<QuestionnaireState>(createDefaultQuestionnaireState);
    const [paymentForm, setPaymentForm] = useState<BillingPaymentFormState>(createDefaultPaymentFormState);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoadedSettings, setHasLoadedSettings] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveConflict, setSaveConflict] = useState<SettingsQuestionnaireSaveConflictState | null>(null);
    const [aiStrategyClearConfirmed, setAiStrategyClearConfirmed] = useState(false);
    const handledPaymentNotificationIdsRef = useRef<Set<string>>(new Set());
    const handledPaymentFailureNotificationIdsRef = useRef<Set<string>>(new Set());
    const settingsSaveInstanceIdRef = useRef<string | null>(null);

    if (!settingsSaveInstanceIdRef.current) {
        const suffix =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
        settingsSaveInstanceIdRef.current = `settings-modal:${suffix}`;
    }

    const applyLoadedSettings = useCallback(
        (data: unknown) => {
            const normalizedSettings = normalizeClientSettingsEntity(data);
            const normalizedState = normalizeQuestionnaireState(normalizedSettings);
            setFormState(normalizedState);
            setPersistedFormState(normalizedState);
            setFeatureControls(normalizedSettings.featureControls);
            setAiQuota(normalizedSettings.aiQuota);
            setSaveConflict(null);
            setAiStrategyClearConfirmed(false);
            return normalizedSettings;
        },
        [setAiQuota, setFeatureControls],
    );

    const refreshSettingsAfterPaymentSuccess = useCallback(
        async (notification: ClientBillingPaymentSuccessNotification) => {
            for (let attempt = 1; attempt <= PAYMENT_SUCCESS_REFRESH_ATTEMPTS; attempt += 1) {
                const response = await settingsApi.get({ authToken: accessToken });
                const normalizedSettings = applyLoadedSettings(response.data);
                setPaymentForm((prevState) => ({ ...prevState, isSubmitting: false }));

                if (hasPaymentMatchingNotification(normalizedSettings.billing, notification)) {
                    settingsQuestionnaireLogger.info(
                        '[client.settings-questionnaire.billing-payment-success] Refreshed limits after payment success.',
                        {
                            clientId,
                            invoiceId: notification.invoiceId,
                            attemptId: notification.attemptId,
                            refreshAttempt: attempt,
                            outcome: 'limits_refreshed',
                        },
                    );
                    toast.success('Оплата прошла. Лимиты обновлены', {
                        autoClose: false,
                        toastId: createBillingPaymentToastId({
                            notificationId: notification.id,
                            outcome: 'success',
                        }),
                    });
                    return;
                }

                if (attempt < PAYMENT_SUCCESS_REFRESH_ATTEMPTS) {
                    await wait(PAYMENT_SUCCESS_REFRESH_DELAY_MS);
                }
            }

            settingsQuestionnaireLogger.info(
                '[client.settings-questionnaire.billing-payment-success] Payment success observed before finalized limits appeared.',
                {
                    clientId,
                    invoiceId: notification.invoiceId,
                    attemptId: notification.attemptId,
                    outcome: 'payment_observed_limits_pending',
                },
            );
            toast.success('Оплата принята. Лимиты обновятся после подтверждения платежа', {
                autoClose: false,
                toastId: createBillingPaymentToastId({
                    notificationId: notification.id,
                    outcome: 'pending',
                }),
            });
        },
        [accessToken, applyLoadedSettings, clientId, settingsApi],
    );

    useEffect(() => {
        const unsubscribeSuccess = subscribeToClientBillingPaymentSuccessNotifications((notification) => {
            if (notification.clientId && clientId && notification.clientId !== clientId) {
                settingsQuestionnaireLogger.debug(
                    '[client.settings-questionnaire.billing-payment-success] Ignored payment success for another client.',
                    {
                        clientId,
                        notificationClientId: notification.clientId,
                        invoiceId: notification.invoiceId,
                    },
                );
                return;
            }

            if (handledPaymentNotificationIdsRef.current.has(notification.id)) {
                return;
            }

            handledPaymentNotificationIdsRef.current.add(notification.id);
            settingsQuestionnaireLogger.info(
                '[client.settings-questionnaire.billing-payment-success] Accepted payment success notification.',
                {
                    clientId,
                    invoiceId: notification.invoiceId,
                    attemptId: notification.attemptId,
                },
            );

            void refreshSettingsAfterPaymentSuccess(notification).catch((error: unknown) => {
                logClientError({
                    logger: settingsQuestionnaireLogger,
                    message:
                        '[client.settings-questionnaire.billing-payment-success] Failed to refresh settings after payment success.',
                    error,
                    context: {
                        clientId,
                        invoiceId: notification.invoiceId,
                        attemptId: notification.attemptId,
                    },
                });
                toast.error('Оплата прошла, но не удалось обновить лимиты. Обновите настройки вручную.', {
                    autoClose: false,
                    toastId: createBillingPaymentToastId({
                        notificationId: notification.id,
                        outcome: 'refresh-failed',
                    }),
                });
            });
        });

        const unsubscribeFailure = subscribeToClientBillingPaymentFailureNotifications(
            (notification: ClientBillingPaymentFailureNotification) => {
                if (notification.clientId && clientId && notification.clientId !== clientId) {
                    settingsQuestionnaireLogger.debug(
                        '[client.settings-questionnaire.billing-payment-failure] Ignored payment failure for another client.',
                        {
                            clientId,
                            notificationClientId: notification.clientId,
                            invoiceId: notification.invoiceId,
                        },
                    );
                    return;
                }

                if (handledPaymentFailureNotificationIdsRef.current.has(notification.id)) {
                    return;
                }

                handledPaymentFailureNotificationIdsRef.current.add(notification.id);
                setPaymentForm((prevState) => ({ ...prevState, isSubmitting: false }));
                settingsQuestionnaireLogger.info(
                    '[client.settings-questionnaire.billing-payment-failure] Accepted payment failure notification.',
                    {
                        clientId,
                        invoiceId: notification.invoiceId,
                        attemptId: notification.attemptId,
                    },
                );
                toast.error('Оплата не завершена. Попробуйте оплатить пакет еще раз.', {
                    autoClose: false,
                    toastId: createBillingPaymentToastId({
                        notificationId: notification.id,
                        outcome: 'failed',
                    }),
                });
            },
        );

        return () => {
            unsubscribeSuccess();
            unsubscribeFailure();
        };
    }, [clientId, refreshSettingsAfterPaymentSuccess]);

    const answeredQuestionsCount = useMemo(
        () => countAnsweredQuestionnaireQuestions(formState.answers),
        [formState.answers],
    );

    const completionProgress = useMemo(() => {
        const filledCoreFields = (formState.storeName.trim() ? 1 : 0) + answeredQuestionsCount;
        const totalCoreFields = QUESTIONNAIRE_QUESTIONS.length + 1;

        return `${filledCoreFields}/${totalCoreFields}`;
    }, [answeredQuestionsCount, formState.storeName]);

    const dirtyTabIds = useMemo(
        () => resolveDirtyEditableTabs(persistedFormState, formState),
        [formState, persistedFormState],
    );

    useEffect(() => {
        return subscribeToClientSettingsSaveNotifications((notification) => {
            if (notification.clientId && clientId && notification.clientId !== clientId) {
                return;
            }

            if (notification.sourceInstanceId === settingsSaveInstanceIdRef.current) {
                return;
            }

            if (notification.version === persistedFormState.version || dirtyTabIds.length === 0) {
                return;
            }

            setSaveConflict({
                message: SETTINGS_CONFLICT_MESSAGE,
                currentVersion: notification.version,
                source: 'browser',
            });
            settingsQuestionnaireLogger.info(
                '[client.settings-questionnaire.settings-save] Observed settings save from another browser context.',
                {
                    clientId,
                    currentVersion: persistedFormState.version,
                    observedVersion: notification.version,
                    dirtyTabIds,
                    dirtyTabCount: dirtyTabIds.length,
                },
            );
        });
    }, [clientId, dirtyTabIds, persistedFormState.version]);

    const loadSettings = useCallback(
        async (signal?: AbortSignal): Promise<void> => {
            setHasLoadedSettings(false);
            setIsLoading(true);

            try {
                const response = await settingsApi.get({ authToken: accessToken, signal });
                applyLoadedSettings(response.data);
                setHasLoadedSettings(true);
            } catch (error) {
                if (isRequestCanceled(error)) {
                    return;
                }

                logClientError({
                    logger: settingsQuestionnaireLogger,
                    message: '[client.settings-questionnaire.settings-load] Failed to load questionnaire settings.',
                    error,
                });
                toast.error('Не удалось загрузить анкету');
                const defaultState = createDefaultQuestionnaireState();
                setFormState(defaultState);
                setPersistedFormState(defaultState);
                setHasLoadedSettings(true);
            } finally {
                setIsLoading(false);
            }
        },
        [accessToken, applyLoadedSettings, settingsApi],
    );

    useEffect(() => {
        if (!isShown) {
            setHasLoadedSettings(false);
            return;
        }

        const controller = new AbortController();
        setHasLoadedSettings(false);
        setIsLoading(true);

        void loadSettings(controller.signal);

        return () => {
            controller.abort();
        };
    }, [isShown, loadSettings]);

    const markLocalEdit = useCallback(() => {
        setSaveConflict(null);
    }, []);

    const onStoreNameChanged = useCallback(
        (value: string) => {
            markLocalEdit();
            setFormState((prevState) => ({ ...prevState, storeName: value }));
        },
        [markLocalEdit],
    );

    const onCompanyInfoLinkChanged = useCallback(
        (value: string) => {
            markLocalEdit();
            setFormState((prevState) => ({ ...prevState, companyInfoLink: value }));
        },
        [markLocalEdit],
    );

    const onEmailChanged = useCallback(
        (value: string) => {
            markLocalEdit();
            setFormState((prevState) => ({ ...prevState, email: value }));
        },
        [markLocalEdit],
    );

    const onCommentsChanged = useCallback(
        (value: string) => {
            markLocalEdit();
            setFormState((prevState) => ({ ...prevState, comments: value }));
        },
        [markLocalEdit],
    );

    const onAnswerChanged = useCallback(
        (questionId: QuestionnaireAnswerId, value: string) => {
            markLocalEdit();
            setAiStrategyClearConfirmed(false);
            setFormState((prevState) => ({
                ...prevState,
                answers: {
                    ...prevState.answers,
                    [questionId]: value,
                },
            }));
        },
        [markLocalEdit],
    );

    const onGeneralSettingChanged = useCallback(
        (settingId: QuestionnaireGeneralSettingId, checked: boolean) => {
            markLocalEdit();
            setFormState((prevState) => ({
                ...prevState,
                settings: {
                    ...prevState.settings,
                    general: {
                        ...prevState.settings.general,
                        [settingId]: checked,
                    },
                },
            }));
        },
        [markLocalEdit],
    );

    const onChannelChanged = useCallback(
        (channelId: QuestionnaireChannelId, checked: boolean) => {
            if (channelId === 'yandexMarket') {
                return;
            }

            markLocalEdit();
            setFormState((prevState) => ({
                ...prevState,
                settings: {
                    ...prevState.settings,
                    marketplaces: {
                        ...prevState.settings.marketplaces,
                        [channelId]: {
                            ...prevState.settings.marketplaces[channelId],
                            enabled: checked,
                        },
                    },
                },
            }));
        },
        [markLocalEdit],
    );

    const onMarketplaceReviewImportLimitChanged = useCallback(
        (marketplaceId: QuestionnaireRuntimeMarketplaceId, value: string) => {
            markLocalEdit();
            setFormState((prevState) => {
                const trimmedValue = value.trim();
                const parsedValue = Number(trimmedValue);
                const max = prevState.settings.marketplaceSettingsConstraints.reviewImportLimitMax;
                const preExistingReviewImportLimit =
                    trimmedValue === '' || !Number.isFinite(parsedValue)
                        ? null
                        : Math.min(max, Math.max(0, Math.trunc(parsedValue)));

                return {
                    ...prevState,
                    settings: {
                        ...prevState.settings,
                        marketplaces: {
                            ...prevState.settings.marketplaces,
                            [marketplaceId]: {
                                ...prevState.settings.marketplaces[marketplaceId],
                                preExistingReviewImportLimit,
                            },
                        },
                    },
                };
            });
        },
        [markLocalEdit],
    );

    const applyPackageToPaymentForm = useCallback(
        (packageId: string) => {
            const selectedPackage = findBillingPackage(formState.billing, packageId);
            const grantedAmounts = selectedPackage
                ? getClientBillingPackageGrantedAmounts(selectedPackage)
                : {
                      aiRequestsAmount: 0,
                      reviewImportsAmount: 0,
                      tokenAmount: 0,
                  };

            setPaymentForm((prevState) => ({
                ...prevState,
                packageId,
                amountMinorUnits: selectedPackage?.priceMinorUnits ?? 0,
                currency: selectedPackage?.currency ?? formState.billing.currency,
                tokenAmount: grantedAmounts.tokenAmount,
                validationMessage: null,
            }));
        },
        [formState.billing],
    );

    const openPaymentForm = useCallback(
        (packageId?: string) => {
            const selectedPackageId = resolveBillingPackageId(formState.billing, packageId);
            const selectedPackage = selectedPackageId ? findBillingPackage(formState.billing, selectedPackageId) : null;
            const grantedAmounts = selectedPackage
                ? getClientBillingPackageGrantedAmounts(selectedPackage)
                : {
                      aiRequestsAmount: 0,
                      reviewImportsAmount: 0,
                      tokenAmount: 0,
                  };

            setPaymentForm({
                ...createDefaultPaymentFormState(formState.billing.currency),
                isOpen: true,
                packageId: selectedPackageId,
                email: formState.email,
                amountMinorUnits: selectedPackage?.priceMinorUnits ?? 0,
                currency: selectedPackage?.currency ?? formState.billing.currency,
                tokenAmount: grantedAmounts.tokenAmount,
            });
        },
        [formState.billing, formState.email],
    );

    const closePaymentForm = useCallback(() => {
        setPaymentForm((prevState) => ({ ...prevState, isOpen: false }));
    }, []);

    const onPaymentPackageChanged = useCallback(
        (packageId: string) => {
            applyPackageToPaymentForm(packageId);
        },
        [applyPackageToPaymentForm],
    );

    const onPaymentEmailChanged = useCallback((value: string) => {
        setPaymentForm((prevState) => ({
            ...prevState,
            email: value,
            validationMessage: null,
        }));
    }, []);

    const submitPayment = useCallback(async () => {
        const selectedPackage = findBillingPackage(formState.billing, paymentForm.packageId);
        const paymentDraft = {
            packageId: paymentForm.packageId,
            email: paymentForm.email.trim(),
        };
        const validationErrors = validateClientBillingRobokassaCheckoutInput(paymentDraft, selectedPackage);
        const validationMessage = getFirstClientBillingValidationMessage(validationErrors);

        setPaymentForm((prevState) => ({
            ...prevState,
            isSubmitting: validationMessage === null,
            validationMessage,
        }));

        if (validationMessage) {
            toast.error(validationMessage);
            return;
        }

        const paymentTarget = reserveExternalPostTarget('robokassa-payment');
        if (!paymentTarget) {
            const errorMessage = 'Не удалось открыть оплату в новой вкладке';
            setPaymentForm((prevState) => ({
                ...prevState,
                isSubmitting: false,
                validationMessage: errorMessage,
            }));
            toast.error(errorMessage);
            return;
        }

        try {
            const response = await billingApi.createRobokassaCheckout(paymentDraft, { authToken: accessToken });
            submitExternalPostFormToTarget(response.data.form, paymentTarget);
            setPaymentForm((prevState) => ({ ...prevState, isOpen: false, isSubmitting: false }));
            toast.success('Оплата открыта в новой вкладке');
        } catch (error) {
            closeReservedExternalPostTarget(paymentTarget);
            logClientError({
                logger: settingsQuestionnaireLogger,
                message: '[client.settings-questionnaire.billing-robokassa-checkout] Failed to start payment.',
                error,
                context: {
                    packageId: paymentDraft.packageId,
                },
            });
            const errorMessage =
                extractApiValidationErrorMessageFromUnknown(error) ??
                extractApiErrorMessageFromUnknown(error) ??
                'Не удалось начать оплату';
            setPaymentForm((prevState) => ({
                ...prevState,
                isSubmitting: false,
                validationMessage: errorMessage,
            }));
            toast.error(errorMessage);
        }
    }, [accessToken, billingApi, formState.billing, paymentForm]);

    const saveEditableTabs = useCallback(
        async (
            tabIds: readonly EditableSettingsTabId[],
            options?: SaveSettingsQuestionnaireTabOptions,
        ): Promise<boolean> => {
            setIsSaving(true);

            try {
                const nextPersistedState = tabIds.reduce<QuestionnaireState>(
                    (nextState, tabId) => mergeQuestionnaireTabState(nextState, formState, tabId),
                    persistedFormState,
                );

                if (tabIds.length === 0) {
                    settingsQuestionnaireLogger.info(
                        '[client.settings-questionnaire.settings-save] Skipped questionnaire settings save because no editable changes were detected.',
                        {
                            clientId,
                            savedTabIds: [],
                            savedTabCount: 0,
                            outcome: 'no_changes',
                        },
                    );
                    if (options?.successMessage !== null) {
                        toast.success(options?.successMessage ?? 'Анкета сохранена');
                    }
                    if (options?.closeAfterSave ?? true) {
                        setIsShown(false);
                    }
                    return true;
                }

                settingsQuestionnaireLogger.info(
                    '[client.settings-questionnaire.settings-save] Accepted questionnaire settings save.',
                    {
                        clientId,
                        savedTabIds: [...tabIds],
                        savedTabCount: tabIds.length,
                        baseVersion: persistedFormState.version,
                    },
                );

                const response = await settingsApi.put(
                    buildSettingsUpdateRequest({
                        baseVersion: persistedFormState.version,
                        tabIds,
                        state: nextPersistedState,
                        clearAiStrategy: tabIds.includes('ai-strategy') && aiStrategyClearConfirmed,
                    }),
                    { authToken: accessToken },
                );
                const normalizedSettings = normalizeClientSettingsEntity(response.data);
                const normalizedState = normalizeQuestionnaireState(normalizedSettings);

                setFormState(normalizedState);
                setPersistedFormState(normalizedState);
                setFeatureControls(normalizedSettings.featureControls);
                setAiQuota(normalizedSettings.aiQuota);
                setSaveConflict(null);
                setAiStrategyClearConfirmed(false);
                publishClientSettingsSaveNotification({
                    clientId,
                    version: normalizedSettings.version,
                    sourceInstanceId: settingsSaveInstanceIdRef.current,
                });
                settingsQuestionnaireLogger.info(
                    '[client.settings-questionnaire.settings-save] Completed questionnaire settings save.',
                    {
                        clientId,
                        savedTabIds: [...tabIds],
                        savedTabCount: tabIds.length,
                        baseVersion: persistedFormState.version,
                        version: normalizedSettings.version,
                    },
                );
                if (options?.successMessage !== null) {
                    toast.success(options?.successMessage ?? 'Анкета сохранена');
                }
                if (options?.closeAfterSave ?? true) {
                    setIsShown(false);
                }
                return true;
            } catch (error) {
                const conflict = isSettingsVersionConflictError(error);
                if (conflict) {
                    settingsQuestionnaireLogger.warn(
                        '[client.settings-questionnaire.settings-save] Rejected questionnaire settings save because loaded settings are stale.',
                        {
                            clientId,
                            savedTabIds: [...tabIds],
                            savedTabCount: tabIds.length,
                            baseVersion: persistedFormState.version,
                            currentVersion: conflict.currentVersion,
                        },
                    );
                    setSaveConflict({
                        message: SETTINGS_CONFLICT_MESSAGE,
                        currentVersion: conflict.currentVersion,
                        source: 'server',
                    });
                    toast.error(SETTINGS_CONFLICT_MESSAGE);
                    return false;
                }

                logClientError({
                    logger: settingsQuestionnaireLogger,
                    message: '[client.settings-questionnaire.settings-save] Failed to save questionnaire settings.',
                    error,
                    context: {
                        savedTabIds: [...tabIds],
                    },
                });
                toast.error('Не удалось сохранить анкету');
                return false;
            } finally {
                setIsSaving(false);
            }
        },
        [
            accessToken,
            aiStrategyClearConfirmed,
            clientId,
            formState,
            persistedFormState,
            setFeatureControls,
            setAiQuota,
            setIsShown,
            settingsApi,
        ],
    );

    const saveTab = useCallback(
        async (tabId: SettingsTabId, options?: SaveSettingsQuestionnaireTabOptions): Promise<boolean> => {
            if (!isEditableSettingsTab(tabId)) {
                return false;
            }

            return saveEditableTabs([tabId], options);
        },
        [saveEditableTabs],
    );

    const saveForm = useCallback(
        async (event: FormEvent<HTMLFormElement>, tabId: SettingsTabId) => {
            event.preventDefault();
            if (!isEditableSettingsTab(tabId)) {
                return;
            }

            await saveEditableTabs(dirtyTabIds, {
                closeAfterSave: true,
                successMessage: 'Анкета сохранена',
            });
        },
        [dirtyTabIds, saveEditableTabs],
    );

    const resetForm = useCallback(
        (tabId: SettingsTabId) => {
            if (tabId === 'ai-strategy') {
                const confirmed = window.confirm(
                    'Очистить сохранённую ИИ-стратегию? Это действие удалит ответы анкеты.',
                );
                if (!confirmed) {
                    return;
                }

                setAiStrategyClearConfirmed(true);
            }

            markLocalEdit();
            const defaultState = createDefaultQuestionnaireState();
            setFormState((prevState) => mergeQuestionnaireTabState(prevState, defaultState, tabId));
        },
        [markLocalEdit],
    );

    const closeModal = useCallback(
        (tabId?: SettingsTabId) => {
            if (tabId && isEditableSettingsTab(tabId)) {
                setFormState((prevState) => mergeQuestionnaireTabState(prevState, persistedFormState, tabId));
            }

            setSaveConflict(null);
            setAiStrategyClearConfirmed(false);
            setIsShown(false);
        },
        [persistedFormState, setIsShown],
    );

    return {
        formState,
        paymentForm,
        questions: QUESTIONNAIRE_QUESTIONS,
        isLoading,
        hasLoadedSettings,
        isSaving,
        saveConflict,
        dirtyTabIds,
        completionProgress,
        onStoreNameChanged,
        onCompanyInfoLinkChanged,
        onEmailChanged,
        onCommentsChanged,
        onAnswerChanged,
        onGeneralSettingChanged,
        onChannelChanged,
        onMarketplaceReviewImportLimitChanged,
        openPaymentForm,
        closePaymentForm,
        onPaymentPackageChanged,
        onPaymentEmailChanged,
        submitPayment,
        saveTab,
        saveForm,
        resetForm,
        closeModal,
        reloadSettings: loadSettings,
    };
};
